'use client';

import React, { useState, useEffect } from 'react';
import { Users, ArrowRight, ArrowLeft, Mail, Lock, Eye, EyeOff, User, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/lib/theme-context';
import { getBackgroundClasses, getTextClasses, getBorderClasses, getInputClasses } from '@/lib/theme-utils';

interface AdminSetupProps {
  domain: string;
  organizationName: string;
  onComplete: (adminEmail: string, adminFirstName: string, adminLastName: string, adminPassword: string) => void;
  onBack: () => void;
  isLoading?: boolean;
  prefillData?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export function AdminSetup({ 
  domain, 
  organizationName, 
  onComplete, 
  onBack, 
  isLoading,
  prefillData 
}: AdminSetupProps) {
  const { theme } = useTheme();
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill form with JWT data when component mounts
  useEffect(() => {
    if (prefillData) {
      if (prefillData.firstName) {
        setAdminFirstName(prefillData.firstName);
      }
      if (prefillData.lastName) {
        setAdminLastName(prefillData.lastName);
      }
      if (prefillData.email && prefillData.email.endsWith(`@${domain}`)) {
        setAdminEmail(prefillData.email);
      } else if (prefillData.email) {
        const userPart = prefillData.email.split('@')[0];
        if (userPart) {
          setAdminEmail(`${userPart}@${domain}`);
        }
      }
    }
  }, [prefillData, domain]);

  // Extract user part from email (before @)
  const getUserPart = (email: string) => {
    return email.split('@')[0] || '';
  };

  // Handle user part change
  const handleUserPartChange = (userPart: string) => {
    const cleanUserPart = userPart.replace(/[^a-zA-Z0-9._-]/g, ''); // Only allow valid email characters
    const fullEmail = `${cleanUserPart}@${domain}`;
    setAdminEmail(fullEmail);
    setError(null);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    // Minimum 15 characters, include uppercase, lowercase, number, special character
    const minLength = password.length >= 15;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    return minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
  };

  const validateName = (name: string) => {
    return name.trim().length >= 1 && name.trim().length <= 50;
  };

  const generateStrongPassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    let password = '';
    
    // Ensure at least one of each required character type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest with random characters from all pools
    const allChars = uppercase + lowercase + numbers + symbols;
    for (let i = 4; i < 20; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password to avoid predictable patterns
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setAdminPassword(password);
    setShowPassword(true); // Show the generated password briefly
    setError(null);
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    if (password.length >= 15) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;
    
    if (score <= 2) return { score, label: 'Weak', color: '' };
    if (score <= 3) return { score, label: 'Fair', color: '' };
    if (score <= 4) return { score, label: 'Good', color: '' };
    return { score, label: 'Strong', color: '' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!adminEmail || !adminFirstName || !adminLastName || !adminPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!validateEmail(adminEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!validateName(adminFirstName)) {
      setError('First name must be between 1 and 50 characters');
      return;
    }

    if (!validateName(adminLastName)) {
      setError('Last name must be between 1 and 50 characters');
      return;
    }

    if (!validatePassword(adminPassword)) {
      setError('Password must be at least 15 characters and include uppercase, lowercase, number, and special character');
      return;
    }

    // Ensure admin email matches the domain
    const emailDomain = adminEmail.split('@')[1];
    if (emailDomain !== domain) {
      setError(`Admin email must be from the ${domain} domain`);
      return;
    }

    setError(null);
    onComplete(adminEmail, adminFirstName.trim(), adminLastName.trim(), adminPassword);
  };

  const passwordStrength = getPasswordStrength(adminPassword);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-4">
        <div className="mx-auto w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mb-2">
          <Users className="w-5 h-5 text-white" />
        </div>
        <h2 className={`text-lg font-bold ${getTextClasses(theme)} mb-1`}>
          Set Up Admin Account
        </h2>
        <p className={`${getTextClasses(theme, 'muted')} text-xs`}>
          Create the administrator account for {organizationName}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Admin First Name */}
        <div className="space-y-1">
          <Label htmlFor="adminFirstName" className={`${getTextClasses(theme)} text-xs`}>
            First Name *
          </Label>
          <div className="relative">
            <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 ${getTextClasses(theme, 'muted')}`} />
            <Input
              id="adminFirstName"
              type="text"
              value={adminFirstName}
              onChange={(e) => {
                setAdminFirstName(e.target.value);
                setError(null);
              }}
              placeholder="John"
              className={`pl-8 ${getInputClasses(theme)} h-9 text-sm`}
              disabled={isLoading}
              required
            />
          </div>
        </div>

        {/* Admin Last Name */}
        <div className="space-y-1">
          <Label htmlFor="adminLastName" className={`${getTextClasses(theme)} text-xs`}>
            Last Name *
          </Label>
          <div className="relative">
            <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 ${getTextClasses(theme, 'muted')}`} />
            <Input
              id="adminLastName"
              type="text"
              value={adminLastName}
              onChange={(e) => {
                setAdminLastName(e.target.value);
                setError(null);
              }}
              placeholder="Doe"
              className={`pl-8 ${getInputClasses(theme)} h-9 text-sm`}
              disabled={isLoading}
              required
            />
          </div>
        </div>

        {/* Admin Email - User Part Only */}
        <div className="space-y-1">
          <Label htmlFor="adminEmailUser" className={`${getTextClasses(theme)} text-xs`}>
            Admin Email Address *
          </Label>
          <div className="flex items-center">
            <div className="relative flex-1">
              <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 ${getTextClasses(theme, 'muted')}`} />
              <Input
                id="adminEmailUser"
                type="text"
                value={getUserPart(adminEmail)}
                onChange={(e) => handleUserPartChange(e.target.value)}
                placeholder="admin"
                className={`pl-8 pr-2 ${getInputClasses(theme)} h-9 text-sm rounded-r-none`}
                disabled={isLoading}
                required
              />
            </div>
            <div className={`${getBackgroundClasses(theme, 'card')} ${getTextClasses(theme)} px-3 py-2 h-9 text-sm ${getBorderClasses(theme)} border-l-0 rounded-r-md flex items-center`}>
              @{domain}
            </div>
          </div>
          <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
            Enter the username part of your email address
          </p>
        </div>

        {/* Admin Password */}
        <div className="space-y-1">
          <Label htmlFor="adminPassword" className={`${getTextClasses(theme)} text-xs`}>
            Admin Password *
          </Label>
          <div className="relative">
            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 ${getTextClasses(theme, 'muted')}`} />
            <Input
              id="adminPassword"
              type={showPassword ? 'text' : 'password'}
              value={adminPassword}
              onChange={(e) => {
                setAdminPassword(e.target.value);
                setError(null);
              }}
              placeholder="Enter a strong password"
              className={`pl-8 pr-20 ${getInputClasses(theme)} h-9 text-sm`}
              disabled={isLoading}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute right-12 top-1/2 transform -translate-y-1/2 ${getTextClasses(theme, 'muted')}`}
            >
              {showPassword ? (
                <EyeOff className="w-3 h-3" />
              ) : (
                <Eye className="w-3 h-3" />
              )}
            </button>
            <button
              type="button"
              onClick={generateStrongPassword}
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getTextClasses(theme, 'muted')}`}
              title="Generate strong password"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
          
          {/* Generate Password Button */}
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={generateStrongPassword}
              className={`text-xs ${getTextClasses(theme)} h-6 px-2`}
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Generate Strong Password
            </Button>
          </div>
          
          {/* Password Strength Indicator */}
          {adminPassword && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs ">Strength:</span>
                <span className={`text-xs font-medium ${passwordStrength.color}`}>
                  {passwordStrength.label}
                </span>
              </div>
              <div className={`w-full ${getBackgroundClasses(theme, 'card')} rounded-full h-1`}>
                <div 
                  className={`h-1 rounded-full transition-all ${
                    passwordStrength.score <= 2 ? '' :
                    passwordStrength.score <= 3 ? '' :
                    passwordStrength.score <= 4 ? '' : ''
                  }`}
                  style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                />
              </div>
              <div className={`text-xs ${getTextClasses(theme, 'muted')}`}>
                15+ chars, A-Z, a-z, 0-9, special char
              </div>
            </div>
          )}
        </div>

        {/* Simple Validation Status */}
        <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg p-3`}>
          <h4 className={`text-xs font-medium ${getTextClasses(theme)} mb-2`}>Form Status</h4>
          <div className="space-y-1 text-xs">
            <div className="flex items-center justify-between">
              <span className={getTextClasses(theme, 'muted')}>Email:</span>
              <span className={adminEmail && validateEmail(adminEmail) && adminEmail.split('@')[1] === domain ? '' : ''}>
                {adminEmail && validateEmail(adminEmail) && adminEmail.split('@')[1] === domain ? '✓' : '✗'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={getTextClasses(theme, 'muted')}>First Name:</span>
              <span className={adminFirstName && validateName(adminFirstName) ? '' : ''}>
                {adminFirstName && validateName(adminFirstName) ? '✓' : '✗'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={getTextClasses(theme, 'muted')}>Last Name:</span>
              <span className={adminLastName && validateName(adminLastName) ? '' : ''}>
                {adminLastName && validateName(adminLastName) ? '✓' : '✗'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className={getTextClasses(theme, 'muted')}>Password:</span>
              <span className={adminPassword && validatePassword(adminPassword) ? '' : ''}>
                {adminPassword && validatePassword(adminPassword) ? '✓' : '✗'}
              </span>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="/10 border /30 rounded-lg p-2" style={{ backgroundColor: 'var(--theme-error)' }}>
            <div className="flex items-center">
              <Lock className="w-3 h-3  mr-2" />
              <p className="text-xs ">{error}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-2">
          <Button
            type="button"
            onClick={onBack}
            variant="outline"
            className={`flex-1 ${getBackgroundClasses(theme, 'default')} ${getBorderClasses(theme)} ${getTextClasses(theme)} h-9 text-sm`}
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Back
          </Button>
          <Button
            type="submit"
            disabled={!adminEmail || !adminFirstName || !adminLastName || !adminPassword || !validateEmail(adminEmail) || !validateName(adminFirstName) || !validateName(adminLastName) || !validatePassword(adminPassword) || adminEmail.split('@')[1] !== domain || isLoading}
            className="flex-1  hover:from-purple-600 hover:to-purple-700 text-white h-9 text-sm"
          >
            Continue
            <ArrowRight className="ml-1 w-3 h-3" />
          </Button>
        </div>
      </form>

      {/* Help Text */}
      <div className={`mt-3 p-2 ${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg`}>
        <h4 className={`text-xs font-medium ${getTextClasses(theme)} mb-1`}>About the Admin Account</h4>
        <ul className={`text-xs ${getTextClasses(theme, 'muted')} space-y-0.5`}>
          <li>• Full administrative privileges</li>
          <li>• Email must be from {domain}</li>
          <li>• Add more users after setup</li>
          <li>• Choose a strong password</li>
        </ul>
      </div>
    </div>
  );
} 